import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Search, Trash2, UserX, ShieldCheck, Loader2 } from "lucide-react";
import { api, fmtErr } from "../../lib/api";

const BADGE = { pending_verification: "bg-coral-100 text-coral-800 dark:bg-coral-800/40 dark:text-coral-400", verified: "bg-teal-100 text-teal-900 dark:bg-teal-900 dark:text-teal-100", completed: "bg-green-100 text-green-900 dark:bg-green-900 dark:text-green-100", pending: "bg-secondary" };

export default function DataRequests() {
  const [requests, setRequests] = useState([]);
  const [email, setEmail] = useState("");
  const [lookup, setLookup] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/deletion-requests").then((r) => setRequests(r.data)).catch((e) => toast.error(fmtErr(e)));
  useEffect(() => { load(); }, []);

  const doLookup = async (target) => {
    const e = (target || email).toLowerCase().trim();
    if (!e) return;
    try { const r = await api.get("/admin/data-lookup", { params: { email: e } }); setLookup(r.data); }
    catch (err) { toast.error(fmtErr(err)); }
  };

  const act = async (mode, targetEmail, rid) => {
    if (!window.confirm(`${mode === "delete" ? "Permanently DELETE" : "Anonymize"} all records for ${targetEmail}? This cannot be undone.`)) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/data-delete", { email: targetEmail, mode, request_id: rid });
      toast.success(`${mode === "delete" ? "Deleted" : "Anonymized"}: ${Object.entries(r.data.affected).map(([k, v]) => `${v} ${k}`).join(", ")}`);
      load();
      if (lookup) doLookup(lookup.email);
    } catch (e) { toast.error(fmtErr(e)); }
    setBusy(false);
  };

  return (
    <div data-testid="data-requests-page" className="max-w-4xl">
      <h1 className="font-heading font-bold text-2xl tracking-tight flex items-center gap-2"><ShieldCheck className="w-6 h-6 text-primary" /> Data requests (PIPEDA)</h1>
      <p className="text-sm text-muted-foreground mb-6">Deletion requests from renters and a manual lookup tool. Completions are logged with a timestamp.</p>

      {/* Manual tool */}
      <section className="rounded-2xl border border-border bg-card p-6">
        <h2 className="font-heading font-semibold text-lg">Manual lookup & erasure</h2>
        <div className="mt-3 flex gap-2">
          <input data-testid="data-lookup-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && doLookup()} placeholder="Search any email…" aria-label="Email to look up"
            className="flex-1 rounded-xl border border-input bg-background px-4 py-2 text-sm" />
          <button data-testid="data-lookup-btn" onClick={() => doLookup()}
            className="inline-flex items-center gap-1.5 rounded-full bg-primary text-primary-foreground px-5 py-2 text-sm font-semibold"><Search className="w-4 h-4" /> Look up</button>
        </div>
        {lookup && (
          <div data-testid="data-lookup-result" className="mt-4 rounded-xl bg-secondary p-4">
            <p className="text-sm font-semibold">Records for {lookup.email}</p>
            <ul className="mt-2 text-sm space-y-1 text-muted-foreground">
              <li>{lookup.leads.length} lead(s) {lookup.leads.map((l) => `${l.first_name} ${l.last_name} (${l.status})`).join("; ")}</li>
              <li>{lookup.users.length} renter account(s)</li>
              <li>{lookup.contact_messages} contact message(s)</li>
              <li>{lookup.saved_searches} saved search(es)</li>
            </ul>
            <div className="mt-3 flex gap-2">
              <button data-testid="data-anonymize-btn" onClick={() => act("anonymize", lookup.email, null)} disabled={busy}
                className="inline-flex items-center gap-1.5 rounded-full border border-border px-4 py-1.5 text-xs font-semibold"><UserX className="w-3.5 h-3.5" /> Anonymize</button>
              <button data-testid="data-delete-btn" onClick={() => act("delete", lookup.email, null)} disabled={busy}
                className="inline-flex items-center gap-1.5 rounded-full bg-coral-500 text-white px-4 py-1.5 text-xs font-semibold"><Trash2 className="w-3.5 h-3.5" /> Delete permanently</button>
            </div>
          </div>
        )}
      </section>

      {/* Requests */}
      <section className="mt-6 rounded-2xl border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead><tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
            <th className="px-5 py-3">Email</th><th className="px-5 py-3">Requested</th><th className="px-5 py-3">Status</th><th className="px-5 py-3">Completed</th><th className="px-5 py-3"></th>
          </tr></thead>
          <tbody>
            {requests.map((r) => (
              <tr key={r.id} data-testid={`data-request-${r.id}`} className="border-b border-border/60 last:border-0">
                <td className="px-5 py-3 font-medium">{r.email}</td>
                <td className="px-5 py-3 text-xs text-muted-foreground">{r.at?.slice(0, 16).replace("T", " ")}</td>
                <td className="px-5 py-3"><span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${BADGE[r.status] || "bg-secondary"}`}>{(r.status || "pending").replace(/_/g, " ")}</span></td>
                <td className="px-5 py-3 text-xs text-muted-foreground">{r.completed_at ? `${r.completed_at.slice(0, 16).replace("T", " ")} (${r.mode || "delete"})` : "—"}</td>
                <td className="px-5 py-3">
                  {r.status !== "completed" && (
                    <div className="flex gap-1.5">
                      <button data-testid={`req-lookup-${r.id}`} onClick={() => { setEmail(r.email); doLookup(r.email); }} aria-label="Look up records"
                        className="p-1.5 rounded-lg hover:bg-secondary"><Search className="w-4 h-4" /></button>
                      <button data-testid={`req-anonymize-${r.id}`} onClick={() => act("anonymize", r.email, r.id)} disabled={busy} aria-label="Anonymize records"
                        className="p-1.5 rounded-lg hover:bg-secondary"><UserX className="w-4 h-4" /></button>
                      <button data-testid={`req-delete-${r.id}`} onClick={() => act("delete", r.email, r.id)} disabled={busy} aria-label="Delete records"
                        className="p-1.5 rounded-lg text-coral-600 hover:bg-coral-50">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}</button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {!requests.length && <tr><td colSpan={5} className="px-5 py-10 text-center text-muted-foreground">No data requests yet.</td></tr>}
          </tbody>
        </table>
      </section>
    </div>
  );
}
